import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import Modal from '../Modal/Modal';
import Button from '../Button/Button';
import NewsletterV2 from './NewsletterV2';
import NewsletterV2WithImage from './NewsletterV2WithImage';
import browser from '../../utils/browser';
import Sites from '../../constants/Sites';
import NewsletterV2NcModal from './NewsletterV2NcModal';

import { readCookie, setCookie } from '../../helpers/Helper';

import "./NewsletterV2Modal.css";

const ForgotPassword = dynamic(() => import('../../components/ForgotPassword/ForgotPassword'));
const Login = dynamic(() => import('../../components/Login/Login'));

const NewsletterModalV2 = (props) => {
  const {
    hideCloseButton = false,
    disableRenderButton = true,
    inlineStyles = { button: { backgroundColor: '#2C68B0' } },
  } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showToolTipState, setShowToolTip] = useState(false);
  const [state, setState] = useState({
    showLoginModal: false,
    showNlModal: true,
    openForgotPasswordModal: false,
    pageHeight: null,
    renderNLModal: true,
  });

  const modalClosedByUser = readCookie('modalClosedByUser');

  const getScrollPercentage = () => {
    let h = document.documentElement,
      b = document.body,
      st = 'scrollTop',
      sh = 'scrollHeight';
    let percent = Math.ceil(
      ((h[st] || b[st]) / ((h[sh] || b[sh]) - h.clientHeight)) * 100
    );
    setState((prevState) => ({ ...prevState, pageHeight: percent }));
  };

  useEffect(() => {
    window.addEventListener('scroll', getScrollPercentage);
    return () => window.removeEventListener('scroll', getScrollPercentage); // Clean up
  }, []);

  useEffect(() => {
    if (
      props.modalProps.displayModalAction === 'timer' &&
      !modalClosedByUser &&
      state.renderNLModal
    ) {
      let pageViews = window.sessionStorage.getItem('pageViews');
      if (
        !props.modalProps.nonClosableModal &&
        (!pageViews || parseInt(pageViews) === 1)
      ) {
        setTimeout(() => {
          setIsModalOpen(true);
        }, parseInt(props.modalProps.displayModalTimeout || 10));
      } else if (props.modalProps.nonClosableModal) {
        setTimeout(() => {
          setIsModalOpen(true);
        }, parseInt(props.modalProps.displayModalTimeout || 10));
      }
    }
  }, [
    props.modalProps.displayModalAction,
    props.modalProps.displayModalTimeout,
  ]);

  useEffect(() => {
    if (
      props.modalProps.scrollPercentage &&
      state.pageHeight >= parseInt(props.modalProps.scrollPercentage) &&
      !modalClosedByUser &&
      state.renderNLModal
    ) {
      setIsModalOpen(true);
    }
  }, [props.modalProps.scrollPercentage, state.pageHeight]);

  const toggleModal = () => {
    if (props.modalClickFunction) {
      props.modalClickFunction();
    }
    setIsModalOpen(!isModalOpen);
    if (props.subComponentType === 'modal') {
      setCookie(
        'modalClosedByUser',
        true,
        process.env.SUBSCRIBED_COOKIE_EXPIRY_DAYS
      );
    }
  };

  const showToolTip = (e) => {
    // Renamed showToolTip function
    e.preventDefault();
    setShowToolTip(!showToolTipState);
  };

  const renderToolTip = () => {
    if (props.followButton && showToolTipState) {
      // Updated to use showToolTipState
      return <div className="tool-tip">{props.toolTipMessage}</div>;
    }
    return null;
  };

  const renderNewsletterCopy = () => {
    if (props.header && props.isFunnelPage) {
      let newsletterCopy =
        typeof props.header === 'string'
          ? `<p>${props.header}</p>`
          : props.header;
      return (
        <div
          className="newsletter-copy"
          dangerouslySetInnerHTML={{ __html: newsletterCopy}}
        />
      );
    }
    return null;
  };

  let shouldDisplayModal = false;
  // E.L
  // only render newsletter modal if it was checked off in the admin and if we are on a single article
  // FYI if a user views an article and that article has a newsletter modal... the modal will fire but not for subsequent articles that are loaded and have a modal.
  if (browser.isClient()) {
    // Only show newsletter modal on the first article view
    // As of 01/11/2020 EBB will start using our popup modal instead of popup modals provided by Digioh
    shouldDisplayModal =
      (!props.isMobile && isModalOpen) ||
      props.modalProps.displayModalAction === 'click' ||
      (props?.modalWithImageProps?.image?.url && isModalOpen);
  }

  const handleBackButtonClick = (val) => {
    if (val === 'login') {
      setState((prevState) => ({
        ...prevState,
        showLoginModal: false,
        openForgotPasswordModal: false,
      }));
    }
    if (val === 'forgotpassword') {
      setState((prevState) => ({
        ...prevState,
        showLoginModal: true,
        openForgotPasswordModal: false,
      }));
    }
  };

  const closeLoginModal = () => {
    setState((prevState) => ({
      ...prevState,
      showLoginModal: false,
      openForgotPasswordModal: false,
      showNlModal: false,
    }));
  };

  const renderLoginModal = () => {
    if (state.showLoginModal) {
      return (
        <Login
          openForgotPasswordModal={openForgotPasswordModal}
          redirectPage={`/${props.slug}`}
          backButtonClicked={handleBackButtonClick}
          isNonClosable={props.modalProps.nonClosableModal}
          closeLoginModal={closeLoginModal}
        />
      );
    }
  };
  const openForgotPasswordModal = () => {
    setState((prevState) => ({
      ...prevState,
      openForgotPasswordModal: true,
      showLoginModal: false,
    }));
  };
  const toggleLoginModal = (e) => {
    e.preventDefault();
    setState((prevState) => ({ ...prevState, showLoginModal: true }));
  };

  const renderNewsletterModal = () => (
    <div className={props.newsletterClasses}>
      {renderNewsletterCopy()}
      {(!disableRenderButton ||
        props.modalProps.displayModalAction === 'click') && (
        <div className="button-wrapper">
          {renderToolTip()}
          {props.icon ? (
            <img
              onClick={toggleModal}
              className="follow-icon"
              src="https://orion-uploads.openroadmedia.com/frontend-assets/follow-icon.png"
              alt="Follow Icon"
              onMouseEnter={showToolTip}
              onMouseLeave={showToolTip}
            />
          ) : (
            <Button
              className="button"
              text={props.buttonText}
              handleClick={toggleModal}
              onMouseEnter={showToolTip}
              onMouseLeave={showToolTip}
              inlineStyles={inlineStyles.button}
            />
          )}
        </div>
      )}
      {
        <Modal
          isOpen={isModalOpen}
          closeBtn={!props.disableToggleModal && toggleModal}
          sizeClass={'small'}
          className={'newsletter-modal autoPopUp'}
          transitionName={'modal-anim'}
          hideCloseButton={hideCloseButton}
          isNonClosable={props.modalProps.nonClosableModal}
        >
          {props.modalWithImageProps.image.url ? (
            props.modalProps.nonClosableModal ? (
              <NewsletterV2NcModal {...props} setIsModalOpen={setIsModalOpen}>
                <NewsletterV2WithImage {...props} toggleModal={toggleModal} />
              </NewsletterV2NcModal>
            ) : (
              <NewsletterV2WithImage {...props} toggleModal={toggleModal} />
            )
          ) : (
            <>
              {!state.showLoginModal &&
                !state.openForgotPasswordModal &&
                // <NewsletterV2 {...props} toggleModal={toggleModal} />
                (props.modalProps.nonClosableModal ? (
                  <NewsletterV2NcModal
                    {...props}
                    setIsModalOpen={setIsModalOpen}
                  >
                    <NewsletterV2 {...props} toggleModal={toggleModal} />
                  </NewsletterV2NcModal>
                ) : (
                  <NewsletterV2 {...props} toggleModal={toggleModal} />
                ))}
              {props.siteId === Sites.EARLYBIRDBOOKS &&
                props.modalProps.nonClosableModal &&
                !state.showLoginModal &&
                !state.openForgotPasswordModal && (
                  <a
                    className="subscribe_message_nonclosablemodal"
                    onClick={toggleLoginModal}
                  >
                    Already a subscriber? Login here
                  </a>
                )}
              {!state.openForgotPasswordModal &&
                state.showLoginModal &&
                renderLoginModal()}
              {state.openForgotPasswordModal && state.showLoginModal && (
                <ForgotPassword
                  backButtonClicked={handleBackButtonClick}
                  isNonClosable={props.modalProps.nonClosableModal}
                />
              )}
            </>
          )}
        </Modal>
      }
    </div>
  );
  return (
    <>
      {/* {props.modalProps.displayModalAction === 'timer' && shouldDisplayModal
        ? renderNewsletterModal()
        : null} */}
      {shouldDisplayModal ? renderNewsletterModal() : null}
    </>
  );
};

NewsletterModalV2.propTypes = {
  displayModalTimeout: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isChild: PropTypes.bool,
  isModalOpen: PropTypes.bool,
  displayModalAction: PropTypes.string,
  onSignUpToggle: PropTypes.func,
  newsletterSignUpButtonText: PropTypes.string,
  newsletterClasses: PropTypes.string,
  icon: PropTypes.bool,
  followButton: PropTypes.bool,
  toolTipMessage: PropTypes.string,
  modalClickFunction: PropTypes.func,
  isFunnelPage: PropTypes.bool,
  inlineStyles: PropTypes.object,
  newsletterCopy: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  hideCloseButton: PropTypes.bool,
  disableRenderButton: PropTypes.bool,
  disableToggleModal: PropTypes.bool,
  isNonClosable: PropTypes.bool,
};

export default NewsletterModalV2;
